/**
 * CriticalTaskList — top panel on the Reports page.
 *
 * Surfaces every audit report currently `needs_revision`. Each row
 * tells the user:
 *   • WHY it was returned (the approver's verbatim reason).
 *   • WHO returned it (so they know who to talk to).
 *   • HOW STALE it is (red badge if ≥ 14 days).
 *
 * 2-step manual rework workflow (2026-06-25):
 *   1. Anyone with access clicks **Resubmit Report** to attest they've
 *      re-filed the corrected report. We stamp `resubmitted_by` on the
 *      row.
 *   2. A supervisor (or higher) clicks **Confirm & Close** to delete
 *      the rejected row + any superseded predecessors so the Audit
 *      Vault stays clean. The close itself is logged in
 *      `audit_events` for traceability.
 *
 * Staff (no role match) see a read-only "Awaiting supervisor"
 * indicator on resubmitted tasks — they can't fire the close action.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, RefreshCw, CheckCircle2, Clock, FileText, ArrowRight,
  Hourglass, Sparkles, Send,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";

function relativeDays(days) {
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } catch { return ""; }
}

export default function CriticalTaskList() {
  const { user } = useAuth();
  const { confirm, notify } = useDialog();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [staleCount, setStaleCount] = useState(0);
  const [awaitingCloseCount, setAwaitingCloseCount] = useState(0);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [actingId, setActingId] = useState("");

  // Step 2 (Confirm & Close) is restricted to supervisor-or-higher
  // per user request. The hierarchy is owner > manager > supervisor
  // > staff, so any of the first three may close.
  const canClose = ["owner", "manager", "supervisor"].includes(user?.role);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/audit-vault/critical-tasks");
      setTasks(data.tasks || []);
      setTotal(data.total || 0);
      setStaleCount(data.stale_count || 0);
      setAwaitingCloseCount(data.awaiting_close_count || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load rework tasks");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const onResubmit = async (task) => {
    const ok = await confirm({
      title: "Mark this rework as resubmitted?",
      message:
        `Confirm you've re-filed the corrected ${task.report_type_label} ` +
        `for ${task.site_name} · ${task.period_label}. ` +
        `\n\nA supervisor (or higher) will then click ` +
        `**Confirm & Close** to remove the rejected record from the vault.`,
      confirmLabel: "Yes, resubmitted",
    });
    if (!ok) return;
    setActingId(task.id);
    try {
      await api.post(`/audit-vault/critical-tasks/${task.id}/resubmit`);
      await notify({
        title: "Marked as resubmitted",
        message: "A supervisor can now confirm and close this task.",
        variant: "ok",
      });
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = (det && typeof det === "object") ? det.message
        : (typeof det === "string" ? det : "Couldn't mark as resubmitted.");
      await notify({ title: "Couldn't mark as resubmitted", message: msg, variant: "err" });
    } finally { setActingId(""); }
  };

  const onClose = async (task) => {
    const ok = await confirm({
      title: "Confirm & close this task?",
      message:
        `The rejected ${task.report_type_label} (${task.site_name} · ` +
        `${task.period_label}) and any superseded predecessors will be ` +
        `deleted from the Audit Vault. The resubmitted report stays.\n\n` +
        `The close action and every deleted record id will be logged ` +
        `in the Activity Log for audit traceability.`,
      confirmLabel: "Confirm & close",
    });
    if (!ok) return;
    setActingId(task.id);
    try {
      const { data } = await api.post(`/audit-vault/critical-tasks/${task.id}/close`);
      await notify({
        title: "Task closed",
        message: `Removed ${data.deleted_count} rejected record(s). The Audit Vault is now clean for this item.`,
        variant: "ok",
      });
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = (det && typeof det === "object") ? det.message
        : (typeof det === "string" ? det : "Couldn't close task.");
      await notify({ title: "Couldn't close task", message: msg, variant: "err" });
    } finally { setActingId(""); }
  };

  if (!busy && total === 0 && !err) {
    return (
      <section
        className="vb-card p-4 mb-6 flex items-center gap-3"
        data-testid="critical-task-list-empty"
        style={{ borderLeft: "3px solid var(--vb-ok, #22c55e)" }}
      >
        <CheckCircle2 size={20} style={{ color: "var(--vb-ok, #22c55e)" }} />
        <div className="min-w-0">
          <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>
            No outstanding rework
          </div>
          <div className="text-xs opacity-70">
            Every audit-vault report is current. Reports returned for rework will appear here with the approver&apos;s reason.
          </div>
        </div>
        <button className="vb-btn xs ghost ml-auto"
          onClick={load} disabled={busy} title="Refresh"
          data-testid="critical-task-list-refresh">
          <RefreshCw size={11} />
        </button>
      </section>
    );
  }

  return (
    <section
      className="vb-card p-5 mb-6"
      data-testid="critical-task-list"
      style={{ borderLeft: `3px solid ${staleCount > 0 ? "var(--vb-error, #ef4444)" : "var(--vb-warn, #f59e0b)"}` }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
          style={{
            background: staleCount > 0 ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)",
            color: staleCount > 0 ? "var(--vb-error)" : "var(--vb-warn, #f59e0b)",
          }}
        >
          <AlertTriangle size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="vb-heading text-xl flex items-center gap-2 flex-wrap"
            style={{ color: "var(--vb-text-2)" }}>
            Critical Task List
            <span className="vb-pill warn text-[10px]" data-testid="critical-task-count">
              {total} returned for rework
            </span>
            {staleCount > 0 && (
              <span className="vb-pill err text-[10px]" data-testid="critical-task-stale-count">
                {staleCount} overdue (≥ 14 days)
              </span>
            )}
            {awaitingCloseCount > 0 && (
              <span className="vb-pill ok text-[10px]" data-testid="critical-task-awaiting-close-count">
                {awaitingCloseCount} awaiting close
              </span>
            )}
          </h2>
          <p className="text-sm opacity-70 max-w-3xl mt-1">
            Read the approver&apos;s reason, fix the underlying data, and re-file the
            report from the assessment panels below. Then click
            <strong> Resubmit Report</strong> here so a supervisor (or higher)
            can <strong>Confirm &amp; Close</strong> the rejected entry — keeping
            the Audit Vault clean for audit.
          </p>
        </div>
        <button className="vb-btn xs ghost" onClick={load} disabled={busy}
          title="Refresh task list" data-testid="critical-task-list-refresh">
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      {err && (
        <div className="vb-pill err mb-3" data-testid="critical-task-list-error">{err}</div>
      )}

      <ul className="grid gap-2">
        {tasks.map((t) => (
          <CriticalTaskRow
            key={t.id} task={t}
            canClose={canClose}
            busy={actingId === t.id}
            onResubmit={() => onResubmit(t)}
            onClose={() => onClose(t)}
          />
        ))}
      </ul>
    </section>
  );
}

function CriticalTaskRow({ task, canClose, busy, onResubmit, onClose }) {
  const stale = task.is_stale;
  const resubmitted = task.is_resubmitted;

  // 2-step state machine:
  //   • Not resubmitted yet → "Resubmit Report" (any role)
  //   • Resubmitted + canClose → "Confirm & Close" (supervisor+)
  //   • Resubmitted + !canClose → disabled "Awaiting supervisor"
  let actionBtn = null;
  if (!resubmitted) {
    actionBtn = (
      <button
        type="button"
        className="vb-btn xs"
        onClick={onResubmit}
        disabled={busy}
        title="Click after you've re-filed the corrected report from the assessment panels below."
        data-testid={`critical-task-resubmit-${task.id}`}
      >
        {busy ? (<><RefreshCw size={11} className="animate-spin" /> Working…</>)
              : (<><Send size={11} /> Resubmit Report</>)}
      </button>
    );
  } else if (canClose) {
    actionBtn = (
      <button
        type="button"
        className="vb-btn xs"
        onClick={onClose}
        disabled={busy}
        title="Delete the rejected record + every superseded predecessor. The resubmitted report stays as audit-of-record."
        data-testid={`critical-task-close-${task.id}`}
      >
        {busy ? (<><RefreshCw size={11} className="animate-spin" /> Closing…</>)
              : (<><Sparkles size={11} /> Confirm &amp; Close</>)}
      </button>
    );
  } else {
    actionBtn = (
      <span className="vb-btn xs ghost whitespace-nowrap"
        style={{ opacity: 0.5, cursor: "not-allowed" }}
        title="Awaiting a supervisor (or higher) to confirm and close this task."
        data-testid={`critical-task-awaiting-close-${task.id}`}>
        <Hourglass size={11} /> Awaiting supervisor
      </span>
    );
  }

  return (
    <li
      className="rounded-md p-3 flex flex-col sm:flex-row sm:items-start gap-3"
      data-testid={`critical-task-row-${task.id}`}
      style={{
        background: "var(--vb-bg, #fff)",
        border: "1px solid var(--vb-border, rgba(0,0,0,0.08))",
        borderLeft: stale
          ? "3px solid var(--vb-error, #ef4444)"
          : resubmitted
            ? "3px solid var(--vb-ok, #22c55e)"
            : "3px solid var(--vb-warn, #f59e0b)",
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <FileText size={13} style={{ color: "var(--vb-accent)" }} className="shrink-0" />
          <strong className="vb-heading text-sm break-words" style={{ color: "var(--vb-text-2)" }}>
            {task.report_type_label}
          </strong>
          <span className="vb-pill text-[10px]" style={{ background: "var(--vb-bg-2, #faf6f1)" }}>
            <Clock size={10} /> {task.period_label}
          </span>
          <span className="vb-pill text-[10px]" style={{ background: "var(--vb-bg-2, #faf6f1)" }}>
            {task.site_name}
          </span>
          <span
            className={`vb-pill ${stale ? "err" : "warn"} text-[10px]`}
            data-testid={`critical-task-age-${task.id}`}
          >
            Returned {relativeDays(task.days_old)}{stale && " · overdue"}
          </span>
          {resubmitted && (
            <span
              className="vb-pill ok text-[10px]"
              data-testid={`critical-task-resubmitted-${task.id}`}
              title="The generator has attested the corrected report has been re-filed."
            >
              <CheckCircle2 size={10} /> Resubmitted by {task.resubmitted_by}
              {task.resubmitted_at && ` · ${relativeTime(task.resubmitted_at)}`}
            </span>
          )}
        </div>
        <div
          className="text-[12px] mt-1 p-2 rounded"
          style={{
            background: "rgba(239,68,68,0.05)",
            borderLeft: "2px solid var(--vb-error, #ef4444)",
          }}
        >
          <span style={{ color: "var(--vb-error)" }}>
            <strong>{task.returned_by}:</strong>
          </span>{" "}
          <em>&ldquo;{task.reason || "No reason recorded."}&rdquo;</em>
        </div>
        <div className="text-[10px] opacity-60 mt-1">
          Original generator: <strong>{task.generator_name}</strong> · Report ID {task.id.slice(0, 8)}
        </div>
      </div>

      <div className="flex flex-col items-stretch sm:items-end gap-1.5 shrink-0">
        {actionBtn}
        <Link
          to="/app/audit-vault"
          className="vb-btn xs ghost whitespace-nowrap"
          title="Inspect the returned record in the Audit Vault (download the original PDF or view the activity log)."
          data-testid={`critical-task-view-${task.id}`}
        >
          View in vault <ArrowRight size={11} />
        </Link>
      </div>
    </li>
  );
}
